import { Button, Col, Row } from "react-bootstrap";
import Card from "../../../../components/Cards/Basic";
import { Form, Formik } from "formik";
import FormSelectBox from "../../../../components/Forms/FormSelectBox";
import FormInputText from "../../../../components/Forms/FormInputText";
import ReservationChildrenFields from "src/components/Forms/ReservationChildrenFields";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../../../config/axios";
import {
  showSuccessToast,
  showValidationErrorToast,
} from "../../../../components/Toasts";
import moment from "moment";
import { keepState } from "../../../../components/UiComponents/Buttons/BackButton/helper";
import history from "../../../../global/history";
import BackButton from "../../../../components/UiComponents/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { DashboardContext } from "../../../../routes/DashboardRoutes";
import ConfirmDialog from "../../../../components/Modal/ConfirmDialog";
import styles from "./EditBookingPage.module.scss";

const dogs = [0, 1, 2, 3].map((value) => ({ value, name: value }));

function dateTimeValues(dateTime) {
  const value = moment(dateTime);
  return {
    date: value.format(moment.HTML5_FMT.DATE),
    time: value.format(moment.HTML5_FMT.TIME),
  };
}

function bookingPeriod(values) {
  return {
    booked_from: `${values.booked_from_date}T${values.booked_from_time}`,
    booked_until: `${values.booked_until_date}T${values.booked_until_time}`,
  };
}

const EditBookingPage = () => {
  const { stayId, id } = useParams();
  const isNew = !id;
  const [booking, setBooking] = useState(null);
  const editReservationGuests = ["requested", "reserved"].includes(
    booking?.stay?.state
  );
  const { settings } = useContext(DashboardContext);
  const { t } = useTranslation("dashboard");
  const [parkingSlotsLoading, setParkingSlotsLoading] = useState(false);
  const [parkingSlots, setParkingSlots] = useState([]);
  const [showWarnParkingSlotDialog, setShowWarnParkingSlotDialog] =
    useState(false);
  const [ageGroups, setAgeGroups] = useState(null);
  const formRef = useRef();
  const startedAt = useRef(moment());

  useEffect(() => {
    if (id) {
      axios
        .get(`/api/v1/bookings/${id}.json`)
        .then(({ data }) => {
          setBooking(data);
          if (data.parking_slot_id) {
            setParkingSlots([
              { ...data.parking_slot, name: data.parking_slot.name },
            ]);
          }
        })
        .catch(console.log);
    }

    axios
      .get("/api/v1/age_groups.json")
      .then(({ data }) => setAgeGroups(data.entries))
      .catch(console.log);
  }, [id]);

  const initialValues = useMemo(() => {
    if (!settings) return null;

    const business = settings.business;
    const start = booking?.booked_from;
    const from = dateTimeValues(start || startedAt.current);
    const defaultUntil =
      business.booking_period_type === "camperstop"
        ? moment(start || startedAt.current).add(24, "hours")
        : moment(start || startedAt.current).add(1, "day");
    const until = dateTimeValues(booking?.booked_until || defaultUntil);

    if (business.booking_period_type === "default" && isNew) {
      from.time = business.checkin_time;
    }
    if (
      business.booking_period_type === "default" &&
      (isNew || booking?.state === "draft")
    ) {
      until.time = business.checkout_time;
    }

    const values = {
      amount_adults: booking?.reservation_adult_count ?? 1,
      reservation_child_ages: booking?.reservation_child_ages || [],
      amount_dogs: booking?.amount_dogs ?? 0,
      parking_slot_id: booking?.parking_slot_id || "",
      booked_from_date: from.date,
      booked_from_time: from.time,
      booked_until_date: until.date,
      booked_until_time: until.time,
    };

    (booking?.booking_age_groups || []).forEach((group) => {
      values[`age_group_${group.age_group_id}`] = group.count;
    });

    return values;
  }, [booking, settings, isNew]);

  function validate(values) {
    const errors = {};
    const period = bookingPeriod(values);
    const from = moment(
      period.booked_from,
      moment.HTML5_FMT.DATETIME_LOCAL,
      true
    );
    const until = moment(
      period.booked_until,
      moment.HTML5_FMT.DATETIME_LOCAL,
      true
    );

    if (
      !moment(values.booked_from_date, moment.HTML5_FMT.DATE, true).isValid()
    ) {
      errors.booked_from_date = "Bitte ein Anreisedatum eingeben.";
    }
    if (
      !moment(values.booked_until_date, moment.HTML5_FMT.DATE, true).isValid()
    ) {
      errors.booked_until_date = "Bitte ein Abreisedatum eingeben.";
    }
    if (
      !moment(values.booked_from_time, moment.HTML5_FMT.TIME, true).isValid()
    ) {
      errors.booked_from_time = "Bitte eine Anreisezeit eingeben.";
    }
    if (
      !moment(values.booked_until_time, moment.HTML5_FMT.TIME, true).isValid()
    ) {
      errors.booked_until_time = "Bitte eine Abreisezeit eingeben.";
    }
    if (from.isValid() && until.isValid() && !until.isAfter(from)) {
      errors.booked_until_date = "Die Abreise muss nach der Anreise liegen.";
    }
    if (editReservationGuests) {
      if (
        !Number.isInteger(Number(values.amount_adults)) ||
        Number(values.amount_adults) < 1
      ) {
        errors.amount_adults = t("booking.guest_validation.adults");
      }
      const ageErrors = values.reservation_child_ages.map((age) =>
        age === "" ||
        !Number.isInteger(Number(age)) ||
        Number(age) < 0 ||
        Number(age) > 17
          ? t("booking.guest_validation.child_age")
          : undefined
      );
      if (ageErrors.some(Boolean)) errors.reservation_child_ages = ageErrors;
    }
    return errors;
  }

  function payload(values) {
    const data = {
      ...bookingPeriod(values),
      amount_dogs: values.amount_dogs,
    };

    if (editReservationGuests) {
      data.amount_guests =
        Number(values.amount_adults) + values.reservation_child_ages.length;
      data.reservation_child_ages = values.reservation_child_ages;
    } else {
      data.booking_age_groups_attributes = ageGroups.map((ageGroup) => {
        const existing = booking?.booking_age_groups?.find(
          (group) => group.age_group_id === ageGroup.id
        );
        return {
          id: existing?.id,
          age_group_id: ageGroup.id,
          count: values[`age_group_${ageGroup.id}`] ?? 0,
        };
      });
    }

    if (settings.business.parking_slots_enabled) {
      data.parking_slot_id = values.parking_slot_id || null;
    }
    if (isNew || booking?.state === "draft") {
      data.state = "active";
    }

    return data;
  }

  async function reloadParkingSlots(values) {
    setParkingSlotsLoading(true);
    try {
      const response = await axios.get("/api/v1/parking_slots.json", {
        params: {
          ...(stayId ? { ignore_occupancy_of_stay_id: stayId } : {}),
          from: bookingPeriod(values).booked_from,
          to: bookingPeriod(values).booked_until,
          active: true,
        },
      });
      setParkingSlots(
        response.data.entries.map((slot) => ({
          ...slot,
          name: `${slot.name} (${slot.is_available ? "Verfügbar" : "Besetzt"})`,
        }))
      );
    } finally {
      setParkingSlotsLoading(false);
    }
  }

  async function saveBooking(values, setSubmitting) {
    try {
      const data = payload(values);
      let response;
      if (id) {
        response = await axios.patch(
          `/api/v1/bookings/${id}/update_with_connected_bookings.json`,
          { booking: data }
        );
      } else {
        response = await axios.post("/api/v1/bookings.json", { booking: data });
      }

      const nextState = keepState(history);
      if (history.location.state?.openBookingOfferId === response.data.id) {
        nextState.openBookingOfferId = response.data.id;
      }
      history.push(`/dashboard/stays/${response.data.stay_id}`, nextState);
      showSuccessToast();
    } catch (error) {
      showValidationErrorToast(error);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleFormSubmit(values, { setSubmitting }) {
    if (settings.business.parking_slots_enabled && values.parking_slot_id) {
      try {
        const response = await axios.get("/api/v1/parking_slots.json", {
          params: {
            ...(stayId ? { ignore_occupancy_of_stay_id: stayId } : {}),
            from: bookingPeriod(values).booked_from,
            to: bookingPeriod(values).booked_until,
            active: true,
          },
        });
        const available = response.data.entries.some(
          (slot) =>
            slot.id === Number(values.parking_slot_id) && slot.is_available
        );
        if (!available) {
          setShowWarnParkingSlotDialog(true);
          setSubmitting(false);
          return;
        }
      } catch (error) {
        showValidationErrorToast(error);
        setSubmitting(false);
        return;
      }
    }
    await saveBooking(values, setSubmitting);
  }

  if (!settings || !ageGroups || (!isNew && !booking)) {
    return <></>;
  }

  const dogField = settings.business.guest_registration_enabled &&
    settings.business.dogs_enabled && (
      <FormSelectBox
        label={t("reservations:editBookingDetails.amountDogs")}
        name="amount_dogs"
        options={dogs}
        option_name="name"
        option_value="value"
        required
      />
    );

  return (
    <Row>
      <Col>
        <Card
          title={
            <div className="d-flex w-100 align-items-center text-center">
              <BackButton
                fallbackRoute={
                  stayId ? `/dashboard/stays/${stayId}` : "/dashboard/bookings/"
                }
              />{" "}
              <p className="mb-0">
                {isNew
                  ? t("booking.new")
                  : t("reservations:editBookingDetails.title")}
              </p>
            </div>
          }
        >
          <Formik
            initialValues={initialValues}
            innerRef={formRef}
            enableReinitialize
            validate={validate}
            onSubmit={handleFormSubmit}
          >
            {({ values, isSubmitting, setSubmitting }) => (
              <Form autoComplete="false" className={styles.form}>
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle}>
                    {t("booking.edit_form.period")}
                  </h2>
                  <div className={styles.periodFields}>
                    <div className={styles.periodGroup}>
                      <FormInputText
                        name="booked_from_date"
                        label={t("booking.edit_form.arrival")}
                        type="date"
                        required
                      />
                      <FormInputText
                        name="booked_from_time"
                        label={t("booking.edit_form.arrival_time")}
                        type="time"
                        required
                      />
                    </div>
                    <div className={styles.periodGroup}>
                      <FormInputText
                        name="booked_until_date"
                        label={t("booking.edit_form.departure")}
                        type="date"
                        required
                      />
                      <FormInputText
                        name="booked_until_time"
                        label={t("booking.edit_form.departure_time")}
                        type="time"
                        required
                      />
                    </div>
                  </div>

                  {settings.business.parking_slots_enabled && (
                    <div className={styles.parkingField}>
                      <FormSelectBox
                        label={t("booking.edit_form.parking_slot")}
                        name="parking_slot_id"
                        options={parkingSlots}
                        loading={parkingSlotsLoading}
                        hint={t("booking.edit_form.parking_slot_hint")}
                        option_name="name"
                        option_value="id"
                        onClick={() => reloadParkingSlots(values)}
                      />
                    </div>
                  )}
                </section>

                <section className={styles.section}>
                  <h2 className={styles.sectionTitle}>
                    {t("booking.edit_form.guests")}
                  </h2>
                  <div className={styles.guestFields}>
                    {editReservationGuests ? (
                      <>
                        <FormInputText
                          name="amount_adults"
                          label={t(
                            "reservations:editBookingDetails.amountAdults"
                          )}
                          type="number"
                          min={1}
                          required
                        />
                        <ReservationChildrenFields
                          className={styles.childrenFields}
                          agesClassName={styles.childAges}
                          afterCount={dogField}
                        />
                      </>
                    ) : (
                      <>
                        {ageGroups.map((ageGroup) => (
                          <FormInputText
                            key={ageGroup.id}
                            name={`age_group_${ageGroup.id}`}
                            label={t(ageGroup.name)}
                            hint={t("editStay.stay_age_groups.hint")}
                            type="number"
                          />
                        ))}
                        {dogField}
                      </>
                    )}
                  </div>
                </section>

                <div className={styles.actions}>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {t("common:components.button.save")}
                  </Button>
                </div>
                {showWarnParkingSlotDialog && (
                  <ConfirmDialog
                    show={true}
                    handleClose={() => setShowWarnParkingSlotDialog(false)}
                    handleSubmit={() => {
                      setShowWarnParkingSlotDialog(false);
                      setSubmitting(true);
                      saveBooking(formRef.current.values, setSubmitting);
                    }}
                    title={t(
                      "online_reservation_details.parkingSlotOverlapDialog.title"
                    )}
                    content={t(
                      "online_reservation_details.parkingSlotOverlapDialog.description"
                    )}
                  />
                )}
              </Form>
            )}
          </Formik>
        </Card>
      </Col>
    </Row>
  );
};

export default EditBookingPage;
